<template>
  <div class="container">
    <el-card class="box-card" :body-style="{ padding: '0' }">
      <!-- 表格头部操作栏 -->
      <div style="display: flex; flex-wrap: wrap; align-items: center; gap: 16px; padding: 0; width: 100%;">
        <!-- 日期范围选择器 -->
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          @change="handleDateRangeChange"
          style="width: 260px; flex-shrink: 0;">
        </el-date-picker>

        <!-- 超级管理员：显示创建人搜索框 -->
        <el-select
          v-if="isSuperAdmin"
          v-model="params.searchCreateAccount"
          placeholder="请选择创建人"
          clearable
          filterable
          @clear="searchCreateAccountClear"
          @change="searchByCreateAccount"
          style="width: 160px; flex-shrink: 0;">
          <el-option
            v-for="user in adminUserList"
            :key="user.userId"
            :label="`${user.userAccount} (${user.roleName})`"
            :value="user.userAccount">
          </el-option>
        </el-select>

        <!-- 批次号搜索 -->
        <el-input
          placeholder="请输入批次号"
          v-model="params.searchBatchNo"
          clearable
          @clear="searchBatchNoClear"
          @keyup.enter.native="searchByBatchNo"
          :style="isSuperAdmin ? 'width: 260px; flex-shrink: 0;' : 'width: 200px; flex-shrink: 0;'">
          <el-button slot="append" icon="el-icon-search" @click="searchByBatchNo"></el-button>
        </el-input>

        <!-- 重置按钮：margin: 0 清除默认间距 -->
        <el-button type="primary" @click="clearSearch" style="flex-shrink: 0; margin: 0;">重置</el-button>

        <!-- 新增批次按钮 -->
        <el-button type="primary" @click="handleAddBatchNoClick" style="flex-shrink: 0; margin: 0;">新增批次</el-button>

        <!-- 删除批次按钮 -->
        <el-button type="danger" @click="batchDelete" style="flex-shrink: 0; margin: 0;">删除批次</el-button>

        <!-- 写入批次号按钮 -->
        <el-button type="success" @click="goToBurnTool" style="flex-shrink: 0; margin: 0;">
          写入批次号
        </el-button>

        <!-- 占位空间：自动占满剩余空间 -->
        <div style="flex: 1;"></div>

        <!-- 刷新按钮：永远在最右边 -->
        <div style="align-self: flex-start; flex-shrink: 0;">
          <el-button type="text" icon="el-icon-refresh" @click="getBatchNoList" style="color: gray; padding: 0; margin: 0;">刷新</el-button>
        </div>
      </div>

      <div class="table-container">
        <el-table
          ref="batchTable"
          :data="displayBatchNoList"
          border
          stripe
          height="100%"
          :row-style="{height: '6vh'}"
          :cell-style="{padding: '0'}"
          @selection-change="handleSelectionChange">

          <!-- 自定义复选框列（原生 checkbox） -->
          <el-table-column
            label=""
            width="55"
            align="center">
            <template #header>
              <input type="checkbox" v-model="selectAll" @change="toggleSelectAll"/>
            </template>
            <template #default="{ row }">
              <input type="checkbox" v-model="selectedIds" :value="row.batchId"/>
            </template>
          </el-table-column>

          <el-table-column label="创建时间" prop="createdTime" :min-width="152" align="center" show-overflow-tooltip>
            <template #default="{ row }">
              {{ formatDateTime(row.createdTime) }}
            </template>
          </el-table-column>

          <el-table-column label="设备批次号" prop="batchNo" :min-width="180" align="center" show-overflow-tooltip></el-table-column>

          <!-- 创建人列：只有超级管理员可见 -->
          <el-table-column v-if="isSuperAdmin" label="创建人" prop="createAccount" :min-width="152" align="center" show-overflow-tooltip></el-table-column>

          <!-- 备注列 -->
          <el-table-column label="批次号备注" :min-width="280" align="center">
            <template slot-scope="{ row }">
              <div class="remark-cell">
                <!-- 超级管理员：可以查看和编辑所有备注 -->
                <template v-if="isSuperAdmin">
                  <el-tooltip
                    effect="dark"
                    :content="row.remark || '-'"
                    placement="top"
                    :disabled="!row.remark || row.remark.length <= 20">
          <span class="remark-text remark-ellipsis">
            {{ row.remark || '-' }}
          </span>
                  </el-tooltip>
                  <el-button
                    type="text"
                    icon="el-icon-edit"
                    size="small"
                    @click="showEditRemarkDialog(row)"
                    style="flex-shrink: 0; padding: 0 4px;">
                  </el-button>
                </template>

                <!-- 普通管理员：只能查看和编辑自己创建的批次号备注 -->
                <template v-else-if="row.createUserId === userId">
                  <el-tooltip
                    effect="dark"
                    :content="row.remark || '-'"
                    placement="top"
                    :disabled="!row.remark || row.remark.length <= 20">
          <span class="remark-text remark-ellipsis">
            {{ row.remark || '-' }}
          </span>
                  </el-tooltip>
                  <el-button
                    type="text"
                    icon="el-icon-edit"
                    size="small"
                    @click="showEditRemarkDialog(row)"
                    style="flex-shrink: 0; padding: 0 4px;">
                  </el-button>
                </template>

                <!-- 非自己创建的批次号，显示空 -->
                <template v-else>
                  <span style="color: #ccc; font-size: 12px;">-</span>
                </template>
              </div>
            </template>
          </el-table-column>

          <!-- 绑定的用户列：普通用户隐藏超级管理员账号 -->
          <el-table-column label="绑定的用户" :min-width="200" align="center">
            <template slot-scope="{ row }">
              <div class="user-cell" v-if="getFilteredUserAccounts(row).length > 0">
                <div class="user-list">
                  <el-tooltip
                    v-for="(userAccount, index) in getFilteredUserAccounts(row).slice(0, 2)"
                    :key="index"
                    effect="dark"
                    :content="userAccount"
                    placement="top"
                    :disabled="userAccount.length <= 8">
                    <el-tag
                      type="primary"
                      size="small"
                      class="user-tag-item"
                      style="cursor: pointer; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                      {{ userAccount }}
                    </el-tag>
                  </el-tooltip>

                  <el-popover
                    v-if="getFilteredUserAccounts(row).length > 2"
                    placement="bottom"
                    width="280"
                    trigger="hover"
                    popper-class="user-popover">
                    <div class="user-popover-content">
                      <div class="user-popover-title">
                        共绑定 {{ getFilteredUserAccounts(row).length }} 个用户
                      </div>
                      <div class="user-popover-list">
                        <el-tooltip
                          v-for="(userAccount, index) in getFilteredUserAccounts(row)"
                          :key="index"
                          effect="dark"
                          :content="userAccount"
                          placement="top"
                          :disabled="userAccount.length <= 8">
                          <el-tag
                            type="primary"
                            size="small"
                            class="user-popover-tag"
                            style="margin: 2px; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: pointer;">
                            {{ userAccount }}
                          </el-tag>
                        </el-tooltip>
                      </div>
                    </div>
                    <el-tag type="warning" size="small" class="user-more-tag" slot="reference">
                      +{{ getFilteredUserAccounts(row).length - 2 }}
                    </el-tag>
                  </el-popover>
                </div>
              </div>
              <span v-else style="color: #999">未绑定</span>
            </template>
          </el-table-column>

          <el-table-column label="绑定数量" :min-width="100" align="center" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <el-tag type="info" size="small">{{ getFilteredBindCount(row) }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页区域 -->
      <div>
        <el-pagination
          ref="pagination"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="params.current"
          :page-sizes="[20, 50, 100, 1000]"
          :page-size="params.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </el-card>

    <!-- 新增批次号对话框 -->
    <el-dialog title="新增设备批次号" :visible.sync="addDialogVisible"
               :modal="false"
               :close-on-click-modal="false"
               width="550px"
               @close="addDialogClosed">
      <el-form ref="addFormRef" :model="addBatchNoForm" :rules="addFormRules" label-width="100px">
        <el-form-item label="设备批次号" prop="batchNo">
          <el-input
            v-model="addBatchNoForm.batchNo"
            clearable
            placeholder="请输入批次号"
          >
          </el-input>
        </el-form-item>
        <el-form-item label="批次号备注" prop="remark">
          <el-input
            v-model="addBatchNoForm.remark"
            type="textarea"
            :rows="3"
            clearable
            placeholder="请输入批次号备注信息（选填）"
            maxlength="200"
            show-word-limit>
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="addDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="addBatchNo" :loading="addBatchNoLoading">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 编辑批次号备注对话框 -->
    <el-dialog title="编辑批次号备注" :visible.sync="editRemarkDialogVisible"
               :modal="false"
               :close-on-click-modal="false"
               width="550px"
               @close="editRemarkDialogClosed">
      <el-form ref="editRemarkFormRef" :model="editRemarkForm" label-width="100px">
        <el-form-item label="设备批次号">
          <el-input v-model="editRemarkForm.batchNo" disabled></el-input>
        </el-form-item>
        <el-form-item label="批次号备注">
          <el-input
            v-model="editRemarkForm.remark"
            type="textarea"
            :rows="4"
            clearable
            placeholder="请输入批次号备注信息"
            maxlength="200"
            show-word-limit>
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editRemarkDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="updateRemark" :loading="updateRemarkLoading">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 批量删除确认对话框 -->
    <el-dialog title="批量删除批次号" :visible.sync="batchDeleteConfirmVisible"
               :modal="false"
               :close-on-click-modal="false"
               width="650px"
               @close="batchDeleteConfirmClosed">
      <div class="delete-warning">
        <div class="warning-icon">
          <i class="el-icon-warning-outline"></i>
        </div>
        <div class="warning-content">
          <!-- 无权限删除的批次号 -->
          <div v-if="noPermissionList.length > 0" class="no-permission-warning">
            <p class="no-permission-text">
              <i class="el-icon-lock"></i>
              以下 <strong>{{ noPermissionList.length }}</strong> 个批次号不是您创建的，无法删除：
            </p>
            <div class="no-permission-list">
              <el-tag
                v-for="(batch, idx) in noPermissionList"
                :key="idx"
                size="small"
                type="danger"
                class="no-permission-tag">
                {{ batch.batchNo }}
              </el-tag>
            </div>
          </div>

          <!-- 需要解绑用户的批次号 -->
          <div v-if="boundBatchList.length > 0" class="binding-warning">
            <p class="binding-text">
              <i class="el-icon-info"></i>
              以下 <strong>{{ boundBatchList.length }}</strong> 个批次号绑定了其他用户，需要解绑后才能删除：
            </p>
            <div class="binding-batch-list-scroll">
              <div
                v-for="(batch, idx) in boundBatchList"
                :key="idx"
                class="binding-batch-item">
                <span class="batch-name">{{ batch.batchNo }}</span>
                <span class="batch-count">(已绑定 {{ batch.needUnbindCount }} 个用户)</span>
                <el-button
                  type="text"
                  size="small"
                  @click="goToUserManagementWithBatch(batch.batchNo)">
                  去解绑
                </el-button>
              </div>
            </div>
            <p class="binding-suggestion">
              <i class="el-icon-link"></i>
              请先前往用户管理解绑这些用户后，再删除批次号。
            </p>
          </div>

          <!-- 可以删除的批次号 -->
          <div v-if="deletableBatchList.length > 0" class="deletable-warning">
            <p class="deletable-text">
              <i class="el-icon-success"></i>
              以下 <strong>{{ deletableBatchList.length }}</strong> 个批次号可以删除：
            </p>
            <div class="deletable-batch-list-scroll">
              <el-tag
                v-for="(batch, idx) in deletableBatchList"
                :key="idx"
                size="small"
                type="success"
                class="deletable-tag">
                {{ batch.batchNo }}
              </el-tag>
            </div>
          </div>

          <!-- 全部无权限 -->
          <div v-if="noPermissionList.length === selectedRows.length && selectedRows.length > 0" class="all-no-permission-text">
            <template v-if="selectedRows.length === 1">
              您选择的批次号不是您创建的，无法删除
            </template>
            <template v-else>
              您选择的 {{ selectedRows.length }} 个批次号都不是您创建的，无法删除
            </template>
          </div>

          <!-- 全部需要解绑 -->
          <div v-else-if="deletableBatchList.length === 0 && boundBatchList.length > 0 && noPermissionList.length === 0" class="all-bound-text">
            <template v-if="boundBatchList.length === 1">
              选中的批次号绑定了其他用户，请先解绑后再删除
            </template>
            <template v-else>
              所有选中的 {{ boundBatchList.length }} 个批次号都绑定了其他用户，请先解绑后再删除
            </template>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="batchDeleteConfirmVisible = false">取 消</el-button>
        <el-button
          type="primary"
          @click="confirmBatchDelete"
          :disabled="deletableBatchList.length === 0"
          :loading="batchDeleteLoading">
          确定删除 {{ deletableBatchList.length }} 个批次号
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import paginationMixin from '@/components/mixins/pagination'

export default {
  mixins: [paginationMixin],
  data() {
    return {
      adminUserList: [], // 管理员和超级管理员用户列表
      selectAll: false, // 全选复选框状态
      selectedIds: [], // 选中的 ID 列表
      dateRange: [], // 日期范围
      params: {
        current: 1,
        size: 20,
        query: {},
        searchBatchNo: '', // 批次号搜索关键字
        searchCreateAccount: '', // 创建人搜索关键字
        startDate: '', // 开始日期
        endDate: '' // 结束日期
      },
      batchNoList: [],
      total: 0,
      selectedRows: [],
      addDialogVisible: false,
      addBatchNoLoading: false, // 新增批次号加载状态
      addBatchNoForm: {
        batchNo: '',
        remark: ''
      },
      addFormRules: {
        batchNo: [
          { required: true, message: '请输入批次号', trigger: 'blur' }
        ],
        remark: [
          { max: 200, message: '备注不能超过200个字符', trigger: 'blur' }
        ]
      },
      editRemarkDialogVisible: false,
      updateRemarkLoading: false, // 更新备注加载状态
      editRemarkForm: {
        batchId: '',
        batchNo: '',
        remark: ''
      },
      batchDeleteConfirmVisible: false,
      batchDeleteLoading: false, // 批量删除加载状态
      noPermissionList: [],
      boundBatchList: [],
      deletableBatchList: []
    }
  },
  computed: {
    ...mapGetters('globalUserInfo', [
      'isAdmin',
      'isSuperAdmin',
      'isNormalAdmin',
      'userAccount',
      'userId',
      'roleId'
    ]),

    // 过滤后的批次号列表
    displayBatchNoList() {
      return this.batchNoList
    }
  },
  async created() {
    // 调用 Vuex 封装方法恢复缓存
    const { params } = await this.$store.dispatch('tabs/restorePageCache', {
      key: 'batchNoCache',
      defaultParams: this.params
    })
    this.params = params

    // 恢复日期范围显示
    if (this.params.startDate && this.params.endDate) {
      this.dateRange = [this.params.startDate, this.params.endDate]
    }

    this.getBatchNoList()
    this.getAdminUserList()
  },
  methods: {
    ...mapActions('tabs', ['savePageCache']),

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      if (typeof dateTime === 'string' && dateTime.includes('T')) {
        return dateTime.replace('T', ' ').substring(0, 19)
      }
      return dateTime
    },

    // 判断是否是超级管理员（基于账号名）
    isSuperAdminByAccount(account) {
      return account === 'admin'
    },

    // 获取管理员和超级管理员用户列表
    async getAdminUserList() {
      try {
        const { data: result } = await this.$http.get('/user/list')
        if (result.status === 200 && result.data && result.data.records) {
          // 筛选 role 为 0（超级管理员）或 1（管理员）
          this.adminUserList = result.data.records.filter(user => {
            const role = Number(user.role)
            return role === 0 || role === 1
          })
        }
      } catch (error) {
        console.error('获取用户列表失败:', error)
      }
    },

    // 获取过滤后的用户账号列表
    getFilteredUserAccounts(row) {
      if (!row.userAccounts || row.userAccounts.length === 0) {
        return []
      }

      if (this.isSuperAdmin) {
        return row.userAccounts
      }

      // 非超级管理员：过滤掉超级管理员账号
      return row.userAccounts.filter(account => !this.isSuperAdminByAccount(account))
    },

    // 获取过滤后的绑定数量
    getFilteredBindCount(row) {
      return this.getFilteredUserAccounts(row).length
    },

    handleSelectionChange(selection) {
      this.selectedRows = selection
    },

    // 全选/取消全选
    toggleSelectAll() {
      if (this.selectAll) {
        // 全选时，选中所有 ID
        this.selectedIds = this.batchNoList.map(item => item.batchId)
      } else {
        // 取消全选时，清空选中列表
        this.selectedIds = []
      }
    },

    // 构建查询参数
    buildQueryParams() {
      const query = {}
      if (this.params.searchBatchNo && this.params.searchBatchNo.trim()) {
        query.batchNo = this.params.searchBatchNo.trim()
      }
      if (this.params.searchCreateAccount && this.params.searchCreateAccount.trim()) {
        query.createAccount = this.params.searchCreateAccount.trim()
      }
      return query
    },

    // 日期范围变化处理
    handleDateRangeChange(value) {
      if (value && value.length === 2) {
        this.params.startDate = value[0]
        this.params.endDate = value[1]
      } else {
        this.params.startDate = ''
        this.params.endDate = ''
      }
      this.params.current = 1
      this.getBatchNoList()
    },

    async getBatchNoList() {
      try {
        // 构建请求参数
        const requestParams = {
          current: this.params.current,
          size: this.params.size,
          query: this.buildQueryParams()
        }

        // 添加日期筛选
        if (this.params.startDate) {
          requestParams.query.startDate = this.params.startDate
        }
        if (this.params.endDate) {
          requestParams.query.endDate = this.params.endDate
        }

        const { data: result } = await this.$http.post('/deviceBatch/page', requestParams)
        if (result.status !== 200) {
          return this.$message.error(result.message)
        }
        this.batchNoList = result.data.deviceBatchPage.records || []
        this.total = result.data.deviceBatchPage.total || 0
        this.clearSelection()

        // 保存缓存
        this.savePageCache({
          key: 'batchNoCache',
          params: this.params
        })
      } catch (error) {
        console.error('获取批次号列表失败:', error)
        this.$message.error('获取批次号列表失败')
      }
    },

    clearSelection() {
      this.selectedIds = []
      this.selectAll = false
      this.selectedRows = []
      if (this.$refs.batchTable) {
        this.$refs.batchTable.clearSelection()
      }
    },

    // 按批次号搜索
    searchByBatchNo() {
      this.params.current = 1
      this.getBatchNoList()
    },

    // 按创建人搜索
    searchByCreateAccount() {
      this.params.current = 1
      this.getBatchNoList()
    },

    searchBatchNoClear() {
      this.params.searchBatchNo = ''
      this.params.current = 1
      this.getBatchNoList()
    },

    searchCreateAccountClear() {
      this.params.searchCreateAccount = ''
      this.params.current = 1
      this.getBatchNoList()
    },

    clearSearch() {
      this.params.searchBatchNo = ''
      this.params.searchCreateAccount = ''
      this.params.startDate = ''
      this.params.endDate = ''
      this.dateRange = []
      this.params.current = 1
      this.getBatchNoList()
    },

    handleAddBatchNoClick() {
      if (!this.isAdmin) {
        this.$message.warning('您没有相关权限，请联系管理员进行操作')
        return
      }
      this.addDialogVisible = true
    },

    addDialogClosed() {
      if (this.$refs.addFormRef) {
        this.$refs.addFormRef.resetFields()
      }
      this.addBatchNoForm.remark = ''
      this.addBatchNoForm.batchNo = ''
      this.addBatchNoLoading = false // 重置 loading 状态
    },

    async addBatchNo() {
      // 防止重复点击
      if (this.addBatchNoLoading) {
        this.$message.warning('正在提交中，请勿重复操作')
        return
      }

      this.$refs.addFormRef.validate(async (valid) => {
        if (!valid) return

        this.addBatchNoLoading = true
        try {
          const { data: result } = await this.$http.post('/deviceBatch/add', this.addBatchNoForm)
          if (result.status !== 200) {
            this.$message.error(result.message)
            return
          }
          this.$message.success(result.message || '新增批次号成功')
          this.addDialogVisible = false
          this.getBatchNoList()
        } catch (error) {
          console.error('新增批次号失败:', error)
          this.$message.error('新增批次号失败')
        } finally {
          this.addBatchNoLoading = false
        }
      })
    },

    batchDelete() {
      if (this.selectedIds.length === 0) {
        this.$message.warning('请先选择要删除的批次号')
        return
      }

      // 根据 selectedIds 获取选中的批次对象
      const selectedBatchList = this.batchNoList.filter(batch => this.selectedIds.includes(batch.batchId))

      const currentUserId = this.userId
      const currentUserAccount = this.userAccount
      const isCurrentUserSuperAdmin = this.isSuperAdmin

      this.noPermissionList = []
      this.boundBatchList = []
      this.deletableBatchList = []

      for (const batch of selectedBatchList) {
        // 超级管理员：可以删除任何批次号，但需要检查是否有其他用户绑定
        if (isCurrentUserSuperAdmin) {
          // 过滤掉超级管理员自己绑定的用户
          const validBindUsers = (batch.userAccounts || []).filter(account => {
            if (this.isSuperAdminByAccount(account)) return false
            return true
          })

          if (validBindUsers.length > 0) {
            this.boundBatchList.push({
              ...batch,
              needUnbindUsers: validBindUsers,
              needUnbindCount: validBindUsers.length
            })
          } else {
            this.deletableBatchList.push(batch)
          }
          continue
        }

        // 非超级管理员：只能删除自己创建的批次号
        if (batch.createUserId !== currentUserId) {
          this.noPermissionList.push(batch)
          continue
        }

        // 检查是否有其他用户绑定（排除自己和超级管理员）
        const validBindUsers = (batch.userAccounts || []).filter(account => {
          if (account === currentUserAccount) return false
          if (this.isSuperAdminByAccount(account)) return false
          return true
        })

        if (validBindUsers.length > 0) {
          this.boundBatchList.push({
            ...batch,
            needUnbindUsers: validBindUsers,
            needUnbindCount: validBindUsers.length
          })
        } else {
          this.deletableBatchList.push(batch)
        }
      }

      if (this.deletableBatchList.length === 0 && this.boundBatchList.length === 0 && this.noPermissionList.length === 0) {
        this.$message.warning('没有可操作的批次号')
        return
      }

      // 检查是否全部都是无权限的（即：选中的批次号全都是不是自己创建的）
      const allNoPermission = this.noPermissionList.length === this.selectedIds.length
      if (allNoPermission) {
        const count = this.selectedIds.length
        this.$message.warning(`您选择的 ${count} 个批次号${count === 1 ? '' : '都'}不是您创建的，无法删除`)
        return
      }

      this.batchDeleteConfirmVisible = true
    },

    async confirmBatchDelete() {
      if (this.deletableBatchList.length === 0) {
        this.$message.warning('没有可删除的批次号')
        return
      }

      // 防止重复点击
      if (this.batchDeleteLoading) {
        this.$message.warning('正在删除中，请勿重复操作')
        return
      }

      this.batchDeleteLoading = true
      try {
        const batchIds = this.deletableBatchList.map(item => item.batchId)
        const { data: result } = await this.$http.post('/deviceBatch/batchDelete', { batchIds })

        if (result.status === 200) {
          let msg = `成功删除 ${this.deletableBatchList.length} 个批次号`
          if (this.noPermissionList.length > 0) {
            msg += `，已过滤 ${this.noPermissionList.length} 个非自己创建的批次号`
          }
          if (this.boundBatchList.length > 0) {
            msg += `，${this.boundBatchList.length} 个批次号因绑定用户未删除`
          }
          this.$message.success(msg)
          this.batchDeleteConfirmVisible = false
          this.clearSelection()

          // 先刷新列表
          await this.getBatchNoList()

          // 删除后检查当前页是否有数据，如果没有且不是第一页，则跳转到上一页
          if (this.batchNoList.length === 0 && this.params.current > 1) {
            this.params.current -= 1
            await this.getBatchNoList() // 重新加载上一页数据
          }
        } else {
          this.$message.error(result.message || '批量删除失败')
        }
      } catch (error) {
        console.error('批量删除失败:', error)
        this.$message.error('批量删除失败，请稍后重试')
      } finally {
        this.batchDeleteLoading = false
      }
    },

    batchDeleteConfirmClosed() {
      this.noPermissionList = []
      this.boundBatchList = []
      this.deletableBatchList = []
      this.batchDeleteLoading = false
    },

    goToUserManagementWithBatch(batchNo) {
      this.batchDeleteConfirmVisible = false
      this.$router.push({
        path: '/user-management',
        query: { batchNo: batchNo }
      })
      this.$message.info(`请在用户管理页面解绑批次号【${batchNo}】的用户`)
    },

    showEditRemarkDialog(row) {
      // 超级管理员：可以编辑所有备注
      if (this.isSuperAdmin) {
        this.editRemarkForm = {
          batchId: row.batchId,
          batchNo: row.batchNo,
          remark: row.remark || ''
        }
        this.editRemarkDialogVisible = true
        return
      }

      // 普通管理员：只能编辑自己创建的批次号备注
      if (row.createUserId !== this.userId) {
        this.$message.warning('您只能编辑自己创建的批次号备注')
        return
      }
      if (!this.isAdmin) {
        this.$message.warning('您没有相关权限，请联系管理员进行操作')
        return
      }
      this.editRemarkForm = {
        batchId: row.batchId,
        batchNo: row.batchNo,
        remark: row.remark || ''
      }
      this.editRemarkDialogVisible = true
    },

    editRemarkDialogClosed() {
      if (this.$refs.editRemarkFormRef) {
        this.$refs.editRemarkFormRef.resetFields()
      }
      this.updateRemarkLoading = false
    },

    async updateRemark() {
      // 防止重复点击
      if (this.updateRemarkLoading) {
        this.$message.warning('正在提交中，请勿重复操作')
        return
      }

      this.updateRemarkLoading = true
      try {
        const { data: result } = await this.$http.post('/deviceBatch/updateRemark', {
          batchId: this.editRemarkForm.batchId,
          remark: this.editRemarkForm.remark
        })
        if (result.status === 200) {
          this.$message.success('备注更新成功')
          this.editRemarkDialogVisible = false
          this.getBatchNoList()
        } else {
          this.$message.error(result.message || '更新失败')
        }
      } catch (error) {
        console.error('更新备注失败:', error)
        this.$message.error('更新备注失败，请稍后重试')
      } finally {
        this.updateRemarkLoading = false
      }
    },

    /**
     * 跳转到烧录工具页面
     */
    goToBurnTool() {
      const selectedBatchList = this.batchNoList.filter(batch => this.selectedIds.includes(batch.batchId))
      if (selectedBatchList.length === 0) {
        this.$router.push({ path: '/serialBurn-tool' })
        return
      }
      if (selectedBatchList.length > 1) {
        this.$message.warning('写入批次号只能选择一条数据，请重新选择')
        return
      }
      this.$router.push({
        path: '/serialBurn-tool',
        query: {
          batchNo: selectedBatchList[0].batchNo
        }
      })
    },

    handleSizeChange(newSize) {
      this.params.size = newSize
      this.getBatchNoList()
    },

    handleCurrentChange(newPage) {
      this.params.current = newPage
      this.getBatchNoList()
    }
  },
  watch: {
    selectedIds(newVal) {
      // 如果数据列表为空，则清空 selectedIds 并将 selectAll 设置为 false
      if (this.batchNoList.length === 0) {
        this.selectAll = false
        return
      }
      // 如果所有行都被选中，则更新 selectAll 为 true
      this.selectAll = newVal.length === this.batchNoList.length
    }
  }
}
</script>

<style lang="less" scoped>
/* 样式保持不变 */
.button-wrapper {
  height: 3.8vh;
  width: 100%;
  text-align: center;
  white-space: nowrap;
}

.button-item {
  display: inline-block;
  height: 100%;
  width: 6.3vh;
  vertical-align: middle;
  text-align: center;
}

.button-item .el-button {
  font-size: 1.5vh;
  height: 100%;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
}

/* 备注样式 */
.remark-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  max-width: 100%;
}

.remark-text {
  display: inline-block;
  max-width: 280px;
  word-break: break-all;
}

.remark-ellipsis {
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 1; /* 允许文本收缩 */
  min-width: 0; /* 允许文本收缩到更小 */
}

/* 用户单元格样式 */
.user-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.user-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.user-tag-item {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}

.user-more-tag {
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: #e6a23c;
    color: #fff;
  }
}

/* 删除确认对话框样式 */
.delete-warning {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 10px 0;
}

.warning-icon {
  flex-shrink: 0;

  i {
    font-size: 48px;
    color: #e6a23c;
  }
}

.warning-content {
  flex: 1;
  max-height: 50vh;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

.warning-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 12px;
}

/* 无权限样式 */
.no-permission-warning {
  background-color: #fef0f0;
  border-left: 3px solid #f56c6c;
  padding: 12px;
  border-radius: 4px;
  margin-top: 10px;
}

.no-permission-text {
  color: #f56c6c;
  font-size: 14px;
  margin-bottom: 10px;

  i {
    margin-right: 6px;
  }

  strong {
    color: #f56c6c;
  }
}

.no-permission-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.no-permission-tag {
  cursor: default;
}

/* 需要解绑样式 */
.binding-warning {
  background-color: #fdf6ec;
  border-left: 3px solid #e6a23c;
  padding: 12px;
  border-radius: 4px;
  margin-top: 10px;
}

.binding-text {
  color: #e6a23c;
  font-size: 14px;
  margin-bottom: 10px;

  i {
    margin-right: 6px;
  }

  strong {
    color: #f56c6c;
  }
}

.binding-batch-list-scroll {
  margin: 12px 0;
  max-height: 200px;
  overflow-y: auto;
  background-color: #fff;
  border-radius: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
}

.binding-batch-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: none;
  }

  .batch-name {
    font-weight: bold;
    color: #303133;
  }

  .batch-count {
    color: #f56c6c;
    font-size: 12px;
  }
}

.binding-suggestion {
  color: #909399;
  font-size: 13px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    color: #409eff;
  }
}

/* 可删除样式 */
.deletable-warning {
  background-color: #f0f9eb;
  border-left: 3px solid #67c23a;
  padding: 12px;
  border-radius: 4px;
  margin-top: 10px;
}

.deletable-text {
  color: #67c23a;
  font-size: 14px;
  margin-bottom: 10px;

  i {
    margin-right: 6px;
  }

  strong {
    color: #67c23a;
  }
}

.deletable-batch-list-scroll {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  max-height: 150px;
  overflow-y: auto;
  padding: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
}

.deletable-tag {
  cursor: default;
}

.all-no-permission-text {
  color: #f56c6c;
  font-size: 14px;
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #fef0f0;
  border-radius: 4px;
}

.all-bound-text {
  color: #e6a23c;
  font-size: 14px;
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #fdf6ec;
  border-radius: 4px;
}
</style>
